// `/demo` guest session — the frontend's presentation layer.
//
// ┌─────────────────────────────────────────────────────────────────────────┐
// │ EVERYTHING IN THIS MODULE IS COSMETIC.                                  │
// │                                                                         │
// │ Read-only is enforced server-side: a default-deny WebSocket allowlist   │
// │ (internal/websocket/demo_guard.go::IsDemoReadOnlyAllowed) plus an HTTP  │
// │ gate (services/server/boot/demo_gate.go). A guest token is a real       │
// │ token, so hiding a control here removes a dead end from the UI — it     │
// │ does not remove a capability. Never weaken, skip, or "optimise away" a  │
// │ server call because this flag says the user is a guest, and never       │
// │ describe anything here as a security boundary.                          │
// └─────────────────────────────────────────────────────────────────────────┘
//
// Design: docs/plans/2026-07-27-demo-guest-session-design.md
// (§4.4 app-shell scope, §6.1 honesty requirements).
//
// This module is deliberately PURE — no store import, no React — so it can be
// consumed by `@/lib/http`, `@/lib/errors/*` and `@/stores/authStore` without
// an import cycle. The derived session flag is published as auth state by
// `authStore.ts` (`useAuthStore(s => s.isDemoGuest)` / `useIsDemoGuest()`).

/** The scope claim a guest access token carries. Set by the backend issuer. */
export const DEMO_READONLY_SCOPE = 'demo_readonly';

/** Machine code the backend returns when it refuses a guest write. */
export const DEMO_READ_ONLY_CODE = 'demo_read_only';

/**
 * The numeric wire code for the refusal — `domain.CodeDemoReadOnly` in
 * `internal/domain/errors.go`.
 *
 * It is a literal here, and it should not have to be. The Go side defines the
 * code but `proto/v2/errors/errors.proto` was NOT extended with a matching
 * `ERROR_CODE_DEMO_READ_ONLY = 220`, so:
 *
 *   - the generated `ErrorCode` enum in this bundle has no member for 220, and
 *     a name lookup returns undefined;
 *   - `wireerr.LegacyResponse` writes `details.error_code` via
 *     `ErrorCode(220).String()`, which for an unknown enum value yields the
 *     decimal string `"220"`, not a name.
 *
 * RESOLVED 2026-07-27: `proto/v2/errors/errors.proto` now declares
 * `ERROR_CODE_DEMO_READ_ONLY = 220`, so `details.error_code` carries the name
 * and the `codeName` path above is the primary match.
 *
 * This numeric match is deliberately KEPT rather than deleted, for one reason:
 * the frontend and backend deploy independently (static export via Angie), so
 * during any rollout a bundle built before the proto change can meet a backend
 * emitting the new code, or the reverse. Matching both the name and the number
 * makes the refusal render correctly through the skew window instead of
 * degrading to a red "something went wrong" toast. Both paths point at one
 * proto member, so there is no second source of truth to drift.
 */
export const DEMO_READ_ONLY_WIRE_CODE = 220;

/** Where a converted guest goes. Absolute — the demo may be reached cross-origin. */
export const DEMO_REGISTER_URL = 'https://app.lumavista.eu/auth/register';

/** The scope-bearing subset of an access token's claims. */
export interface DemoScopeClaims {
  scope?: string;
}

/**
 * isDemoGuestToken reports whether decoded token claims describe a `/demo`
 * guest session.
 *
 * A MISSING scope returns false, and that asymmetry is the point: an ordinary
 * session is full-privilege, so demo mode is an explicit opt-in that can never
 * be triggered by an absent, null, or unparsed field. The equality is strict
 * against the single scope the issuer mints (the backend sets exactly
 * `demo_readonly`, not a space-delimited OAuth scope list) — mirroring
 * `auth.Claims.IsDemoReadOnly()` on the server so the two can't drift into
 * disagreeing about what a guest is.
 */
export function isDemoGuestToken(claims: DemoScopeClaims | null | undefined): boolean {
  return claims?.scope === DEMO_READONLY_SCOPE;
}

function base64UrlDecode(segment: string): string | null {
  if (typeof atob !== 'function') return null;
  try {
    const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const binary = atob(b64 + pad);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * decodeAccessTokenClaims reads the claims segment of a JWT.
 *
 * The signature is NOT verified and deliberately so: this value drives which
 * menu rows render, nothing else. The server re-parses and re-verifies the
 * token on every request. A malformed / non-JWT / tampered token yields null,
 * which `isDemoGuestToken` then reads as "not a guest" — i.e. the full UI, and
 * the server refusing anything that isn't allowed.
 */
export function decodeAccessTokenClaims(
  token: string | null | undefined,
): DemoScopeClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = base64UrlDecode(parts[1] ?? '');
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const scope = (parsed as { scope?: unknown }).scope;
    return typeof scope === 'string' ? { scope } : {};
  } catch {
    return null;
  }
}

// One-entry memo: the access token changes only on login/refresh, but the
// derived flag is read on every auth-store subscription tick.
let memoToken: string | null | undefined;
let memoFlag = false;

/** isDemoGuestAccessToken decodes a raw access token and applies the scope test. */
export function isDemoGuestAccessToken(token: string | null | undefined): boolean {
  if (token === memoToken) return memoFlag;
  memoToken = token;
  memoFlag = isDemoGuestToken(decodeAccessTokenClaims(token));
  return memoFlag;
}

/** Test seam — drops the memo so a suite can re-derive from a fresh token. */
export function resetDemoGuestMemo(): void {
  memoToken = undefined;
  memoFlag = false;
}

/**
 * normaliseErrorCode folds the several shapes a machine code can arrive in
 * (`ERROR_CODE_DEMO_READ_ONLY`, `DEMO_READ_ONLY`, `demo_read_only`, or a Go
 * sentinel's `demo_read_only: this action requires an account`) down to one
 * comparable token.
 */
function normaliseErrorCode(value: string | undefined): string {
  if (!value) return '';
  const head = value.split(':')[0] ?? '';
  return head.trim().toLowerCase().replace(/^error_code_/, '');
}

/**
 * isDemoReadOnlyRefusal reports whether a normalised wire error is the
 * backend's demo refusal. Three carriers, all of which occur:
 *
 *  - **HTTP** — `403 {"error":"demo_read_only","detail":"This action requires
 *    an account."}` from `demo_gate.go`. There is no `error_code`, so the
 *    string lands in `message`.
 *  - **WebSocket, today** — `domain.MapError` maps the typed sentinel to
 *    `CodeDemoReadOnly` (220) with SafeMessage "This action requires an
 *    account.". Since the proto enum has no member for 220 the name is lost on
 *    both carriers, so only the NUMBER identifies it. This is the live path.
 *  - **WebSocket, after the proto gains `ERROR_CODE_DEMO_READ_ONLY`** — the
 *    name arrives as `codeName` from either the enum reverse-lookup or
 *    `details.error_code`.
 *
 * Accepting all three means neither half of this feature can regress the other:
 * the number keeps working now, and the name starts working the moment the
 * proto is regenerated, with no coordinated deploy.
 */
export function isDemoReadOnlyRefusal(
  // Explicit `| undefined` on each field: this is a defensive predicate called
  // with freshly-built literals whose fields may be undefined, which
  // `exactOptionalPropertyTypes` would otherwise reject.
  err:
    | {
        code?: number | undefined;
        codeName?: string | undefined;
        message?: string | undefined;
      }
    | null
    | undefined,
): boolean {
  if (!err) return false;
  return (
    err.code === DEMO_READ_ONLY_WIRE_CODE ||
    normaliseErrorCode(err.codeName) === DEMO_READ_ONLY_CODE ||
    normaliseErrorCode(err.message) === DEMO_READ_ONLY_CODE
  );
}
